import React, { useState } from "react";
import axios from "axios";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import { TID } from "../constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Benefit = ({ icon, text }) => (
    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg px-4 py-3">
        <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center">
            <i className={`fa-solid ${icon}`} />
        </div>
        <span className="text-sm font-semibold text-amber-50">{text}</span>
    </div>
);

const MemberInquiry = () => {
    const tr = useT();
    const { lang } = useLang();
    const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", scheme: "", message: "" });
    const [status, setStatus] = useState({ loading: false, ok: null, error: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: null, error: "" });
        try {
            const payload = Object.fromEntries(
                Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
            );
            const res = await axios.post(`${API}/inquiry`, payload);
            if (res.data?.success) {
                setStatus({ loading: false, ok: true, error: "" });
                setForm({ name: "", mobile: "", email: "", address: "", scheme: "", message: "" });
            } else {
                setStatus({ loading: false, ok: false, error: tr(t.form.error) });
            }
        } catch {
            setStatus({ loading: false, ok: false, error: tr(t.form.error) });
        }
    };

    const schemes = [
        { en: "Fixed Deposit", mr: "मुदत ठेव" },
        { en: "Recurring Deposit", mr: "आवर्ती ठेव" },
        { en: "Monthly Income Plan", mr: "मासिक उत्पन्न योजना" },
        { en: "Daily Savings", mr: "दैनिक ठेव" },
        { en: "Bal Vikas Thev", mr: "बालविकास ठेव" },
        { en: "Personal Loan", mr: "वैयक्तिक कर्ज" },
        { en: "Business Loan", mr: "व्यवसाय कर्ज" },
        { en: "Gold Loan", mr: "सोने तारण कर्ज" },
        { en: "Vehicle Loan", mr: "वाहन कर्ज" }
    ];

    return (
        <div>
            <section className="bg-gradient-to-br from-red-700 via-red-700 to-amber-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-4">
                                <i className="fa-solid fa-user-plus" />
                                {lang === "mr" ? "नवीन सभासद" : "New Member"}
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{tr(t.membership.title)}</h1>
                            <p className="mt-4 text-amber-50 text-base sm:text-lg max-w-xl">{tr(t.membership.subtitle)}</p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Benefit icon="fa-shield-halved" text={lang === "mr" ? "सुरक्षित बचत" : "Safe Savings"} />
                            <Benefit icon="fa-bolt" text={lang === "mr" ? "जलद सेवा" : "Fast Service"} />
                            <Benefit icon="fa-percent" text={lang === "mr" ? "उत्तम व्याज" : "Best Rates"} />
                            <Benefit icon="fa-handshake" text={lang === "mr" ? "वैयक्तिक मार्गदर्शन" : "Personal Guidance"} />
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.name)} *</label>
                                <input type="text" required value={form.name} onChange={update("name")} data-testid={TID.memberName}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.mobile)} *</label>
                                <input type="tel" required value={form.mobile} onChange={update("mobile")} data-testid={TID.memberMobile}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.email)}</label>
                            <input type="email" value={form.email} onChange={update("email")} data-testid={TID.memberEmail}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.address)}</label>
                            <textarea rows={2} value={form.address} onChange={update("address")} data-testid={TID.memberAddress}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none resize-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.scheme)}</label>
                            <select value={form.scheme} onChange={update("scheme")} data-testid={TID.memberScheme}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none bg-white">
                                <option value="">— {lang === "mr" ? "निवडा" : "Select"} —</option>
                                {schemes.map((s, i) => (
                                    <option key={i} value={s.en}>{tr(s)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{tr(t.form.message)}</label>
                            <textarea rows={4} value={form.message} onChange={update("message")} data-testid={TID.memberMessage}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none resize-none" />
                        </div>

                        <button
                            type="submit"
                            disabled={status.loading}
                            data-testid={TID.memberSubmit}
                            className="w-full inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white font-bold px-5 py-3.5 rounded-lg transition-colors shadow-sm"
                        >
                            {status.loading ? tr(t.cta.sending) : tr(t.cta.submit)}
                            {!status.loading && <i className="fa-solid fa-paper-plane" />}
                        </button>

                        {status.ok && (
                            <div
                                data-testid={TID.memberSuccess}
                                className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2"
                            >
                                <i className="fa-solid fa-circle-check" />
                                {tr(t.form.thankYou)}
                            </div>
                        )}
                        {status.error && (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2">
                                <i className="fa-solid fa-triangle-exclamation" />
                                {status.error}
                            </div>
                        )}
                    </form>
                </div>
            </section>
        </div>
    );
};

export default MemberInquiry;
